import { apiFetch } from "@/lib/api";
import { type AuthUser, useAuthStore } from "@/features/auth/store/auth-store";

/**
 * The Django API wraps every body in the standard envelope
 * (`APIJSONRenderer`): `{ success, message, data }`. `apiFetch` returns that
 * envelope verbatim, so this layer reads `.data` and maps the snake_case API
 * shape to the camelCase used in the UI (same convention as other features).
 */
interface Envelope<T> {
  success: boolean;
  message: string;
  data: T;
}

/** Raw /auth/me shape from the API (snake_case), a superset of `AuthUser`. */
interface ApiUser {
  id: string;
  name: string;
  email: string;
  account_type: "firm" | "individual";
  role: "staff" | "team_leader" | "country_director" | null;
  // Stored avatar path (private media). Null when none. Used only as a
  // cache-busting token for the authenticated image endpoint — the bytes load
  // via `/auth/me/avatar`, never this URL directly.
  avatar: string | null;
  date_joined: string;
  language: "en" | "es";
  time_zone: string;
  email_signature: string;
  default_practitioner: string | null;
  default_practitioner_name: string | null;
}

/** The authenticated user's editable profile + preferences, camelCased. */
export interface SettingsUser {
  id: string;
  name: string;
  email: string;
  accountType: "firm" | "individual";
  /** Stored avatar path (private media), or null. Cache-busting token for the
   * authenticated `/auth/me/avatar` image endpoint — not a loadable URL itself. */
  avatarUrl: string | null;
  /** ISO timestamp the account was created (drives "member since"). */
  memberSince: string;
  language: "en" | "es";
  timeZone: string;
  emailSignature: string;
  defaultPractitioner: string | null;
  defaultPractitionerName: string | null;
}

/** The writable subset a PATCH /auth/me accepts. */
export interface PreferencesPayload {
  name: string;
  language: "en" | "es";
  timeZone: string;
  emailSignature: string;
  /** Practitioner id, or null to clear. Firm accounts only. */
  defaultPractitioner: string | null;
}

export const LANGUAGE_OPTIONS: { value: "en" | "es"; label: string }[] = [
  { value: "en", label: "English" },
  { value: "es", label: "Spanish" },
];

/**
 * Time zones offered in the UI — MUST stay in lockstep with the backend's
 * `ALLOWED_TIME_ZONES` (apps/accounts/serializers.py), which rejects anything
 * outside this list on PATCH.
 */
export const TIME_ZONE_OPTIONS: { value: string; label: string }[] = [
  { value: "America/New_York", label: "Eastern Time (US)" },
  { value: "America/Chicago", label: "Central Time (US)" },
  { value: "America/Denver", label: "Mountain Time (US)" },
  { value: "America/Phoenix", label: "Arizona (US)" },
  { value: "America/Los_Angeles", label: "Pacific Time (US)" },
  { value: "America/Anchorage", label: "Alaska (US)" },
  { value: "Pacific/Honolulu", label: "Hawaii (US)" },
  { value: "America/Puerto_Rico", label: "Atlantic (Puerto Rico)" },
  { value: "UTC", label: "UTC" },
  { value: "Europe/London", label: "London (UK)" },
  { value: "Asia/Kolkata", label: "India (IST)" },
];

function toSettingsUser(api: ApiUser): SettingsUser {
  return {
    id: api.id,
    name: api.name,
    email: api.email,
    accountType: api.account_type,
    avatarUrl: api.avatar,
    memberSince: api.date_joined,
    language: api.language,
    timeZone: api.time_zone,
    emailSignature: api.email_signature,
    defaultPractitioner: api.default_practitioner,
    defaultPractitionerName: api.default_practitioner_name,
  };
}

/** Keep the persisted auth-store user in sync so the header/name never go stale.
 *
 * `AuthUser` is intentionally snake_case and shape-compatible with `ApiUser`
 * (its preference fields line up 1:1), so the raw API object can be stored
 * directly without the camelCase mapping the UI uses. Keep the two types in
 * sync — a divergent field type would silently break this cast. */
function syncAuthStore(api: ApiUser) {
  const current = useAuthStore.getState().user;
  useAuthStore.getState().setUser({ ...(current ?? {}), ...(api as AuthUser) });
}

/** GET /auth/me — the authenticated user's full profile + preferences. */
export async function getMe(): Promise<SettingsUser> {
  const { data } = await apiFetch<Envelope<ApiUser>>("/auth/me");
  return toSettingsUser(data);
}

/** PATCH /auth/me — update the editable profile/preferences subset. */
export async function updatePreferences(payload: PreferencesPayload): Promise<SettingsUser> {
  const { data } = await apiFetch<Envelope<ApiUser>>("/auth/me", {
    method: "PATCH",
    json: {
      name: payload.name,
      language: payload.language,
      time_zone: payload.timeZone,
      email_signature: payload.emailSignature,
      default_practitioner: payload.defaultPractitioner,
    },
  });
  syncAuthStore(data);
  return toSettingsUser(data);
}

/** PATCH /auth/me (multipart) — upload/replace the user's profile avatar. */
export async function uploadAvatar(file: File): Promise<SettingsUser> {
  const form = new FormData();
  form.append("avatar", file);
  const { data } = await apiFetch<Envelope<ApiUser>>("/auth/me", {
    method: "PATCH",
    form,
  });
  syncAuthStore(data);
  return toSettingsUser(data);
}

/** POST /auth/change-password — verifies the current password server-side. */
export function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  return apiFetch<void>("/auth/change-password", {
    method: "POST",
    json: { current_password: currentPassword, new_password: newPassword },
  });
}
